import React, {useEffect, useMemo} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';

import {IStore} from 'redux/interface';

import Loader from 'components/loader';
import {useGetTranslations} from '../../utils/hooks/useGetTranslations';
import {Pages, TranslationKeys} from '../../utils/constants/routeConstants';
import {
  activityJournalsAsyncActions,
  getActivityJournals,
  getTranslations
} from '../../redux/pageAsyncActions';

import Icon from 'assets/image/menu-auth/chat.svg';
import NotFoundImg from '../../assets/image/common/notFound.png';
import NotFoundInfo from '../../components/not-found-info';
import {ActivityJournalCard} from '../../components/activity-journal-card/ActivityJournalCard';

import './styles.scss';

export const ActivityJournals: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const {activityJournals} = useSelector((state: IStore) => state.activity_journals);
  const {settings} = useSelector((state: IStore) => state.auth);
  const {loading} = useSelector((state: IStore) => state.status);
  const [filter, setFilter] = React.useState('all');

  const pageConfig = useMemo(() => {
    return settings?.pages.find(
      (p) => p.name.toLowerCase() === Pages.ActivityJournals.toLowerCase()
    );
  }, [settings]);

  const translationKey = pageConfig?.translationKey || TranslationKeys.ActivityJournals;
  const title = pageConfig?.title || Pages.ActivityJournals;

  useGetTranslations({translationKey});

  useEffect(() => {
    dispatch(activityJournalsAsyncActions[getActivityJournals]());
  }, [dispatch]);

  const isTranslationsLoading = loading.includes(getTranslations);
  const isActivitiesLoading = loading.includes(getActivityJournals);

  const filtered = activityJournals.filter(a => {
    if (filter === 'completed') {
      return a.percentage === 100;
    }
    if (filter === 'in_progress') {
      return !!a.dialogId;
    }

    return true;
  });

  if (isTranslationsLoading) {
    return <Loader nested />;
  }

  return (
    <section className="activity-journals container">
      <h1 className="activity-journals__title">
        <img src={Icon} alt="" className="activity-journals__icon" />
        <FormattedMessage
          id={`gritx.common.navigation.${title}`}
          defaultMessage={title}
        />
      </h1>

      {isActivitiesLoading && <div className="activity-journals__loader">
        <Loader nested/>
      </div>}
      {!isActivitiesLoading && activityJournals.length === 0 && <div className="activity-journals__empty">
        <NotFoundInfo
          title={intl.formatMessage({
            id: 'gritx.activityJournals.notFound',
            defaultMessage: 'Sorry, but nothing can be found.'
          })}
          image={NotFoundImg}
        />
      </div>}
      {!isActivitiesLoading && activityJournals.length > 0 && <>
        <div className="activity-journals__filters">
          <button className={`activity-journals__filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
            <FormattedMessage
              id={'gritx.activityJournals.all'}
              defaultMessage="All"
            />
          </button>
          <button className={`activity-journals__filter-btn ${filter === 'in_progress' ? 'active' : ''}`}
            onClick={() => setFilter('in_progress')}>
            <FormattedMessage
              id={'gritx.activityJournals.inProgress'}
              defaultMessage="In progress"
            />
          </button>
          <button className={`activity-journals__filter-btn ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}>
            <FormattedMessage
              id={'gritx.activityJournals.completed'}
              defaultMessage="Completed"
            />
          </button>
        </div>

        <div className="activity-journals__list">
          {filtered.map(a => (
            <ActivityJournalCard key={a.xpeditionId} journal={a} />
          ))}
        </div>
      </>}
    </section>
  );
};
